import React, { useCallback, useContext, useEffect, useState } from 'react';
import { View } from 'react-native';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';
import { DateTime } from 'luxon';
import styled from 'styled-components/native';
import DataTable from '@preflighttech/preflight-tables';

import { AuthContext } from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { Fields, defaultValues } from 'app/src/elements/hookForms';
import { StyledTextInput } from 'app/src/elements/inputs';
import minimalTable from 'app/src/elements/minimalTable';
import NestedFields from 'app/src/elements/NestedFields';
import MutationErrors from 'app/src/elements/MutationErrors';
import DataState from 'app/src/components/DataState';
import { Text } from 'app/src/styles';
import { currency } from 'app/src/utils/stringHelpers';

import { PAYMENT_SELECTS, TXN_BY_REFERENCE } from './queries';

import { CREATE_TXN } from '../Txns/queries';
import METHODS from '../Txns/methods';
import { prepareTxnInput } from '../Txns/utils';

export const Section = styled(View)`
  margin-top: 10px;
`;

export const FIELDS = {
  bankDate: {
    label: 'Date',
    type: 'date',
    rules: { required: true },
  },
  balanceAccountId: {
    label: 'Payment Account',
    type: 'account',
    rules: { required: true },
    preferredTypes: [
      'Bank', 'Accounts Payable', 'Credit Card', 'Other Current Asset'
    ],
    preferredOnly: true,
  },
  method: {
    label: 'Payment Method',
    select: METHODS,
    rules: { required: true },
  },
  reference: {
    label: 'Reference / Incoming Wire Title',
  },
  name: {
    type: 'entityName',
    preferredTypes: ['Buyer'],
  },
  memo: {},
};

export const LINE_FIELDS = {
  accountId: {
    label: 'Account',
    type: 'account',
    preferredTypes: ['Income'],
  },
  creditLessDebit: {
    label: 'Amount',
  },
  assetId: {
    label: 'Asset',
    type: 'asset',
    width: 140,
    extraLabels: {
      saleAssetId: 'Lot/Sale',
      feeId: 'Fee',
    },
  },
  memo: { },
};

export const EMPTY_LINE = {
  accountId: '',
  creditLessDebit: '',
  memo: '',
};

export const invoiceColumns = ({ invoiceAmounts, setInvoiceAmounts }) => (
  [
    {
      key: 'bankDate',
      label: 'Date',
      sort: 'asc',
      value: ({ entry }) => entry.txn.bankDate,
    },
    {
      key: 'reference',
      label: 'Reference',
      value: ({ entry }) => entry.txn.reference,
    },
    {
      key: 'name',
      label: 'Name',
      value: ({ entry }) => entry.txn.name,
    },
    {
      key: 'amount',
      label: 'Amount',
      content: ({ value }) => currency(value),
    },
    {
      key: 'openBalance',
      label: 'Open Balance',
      content: ({ value }) => currency(value),
    },
    {
      key: 'pay',
      label: 'Pay?',
      content: ({ entry }) => {
        const isChecked = !!(invoiceAmounts.find(item => item.id === entry.id));

        return (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => {
              if (isChecked) {
                setInvoiceAmounts(
                  [...invoiceAmounts.filter(item => item.id !== entry.id)]
                );
              } else {
                setInvoiceAmounts([
                  ...invoiceAmounts,
                  { id: entry.id, amount: entry.openBalance }
                ]);
              }
            }}
          />
        );
      }
    },
    {
      key: 'payAmount',
      label: 'Pay Amount',
      content: ({ entry }) => {
        const invoiceAmount = invoiceAmounts.find(item => item.id === entry.id);

        if (!invoiceAmount) { return null; }

        return (
          <input
            type="text"
            value={invoiceAmount.amount}
            onChange={e => {
              invoiceAmount.amount = e.target.value;
              setInvoiceAmounts([...invoiceAmounts]);
            }}
          />
        );
      }
    },
  ]
);

const NewPayment = ({ setShowModal, refetch }) => {
  const { accounts } = useContext(AuthContext);

  const emptyLines = [{...EMPTY_LINE}];
  const [invoiceAmounts, setInvoiceAmounts] = useState([]);
  const [nestedResources, setNestedResources] = useState(emptyLines);
  const [formStatus, setFormStatus] = useState();
  const [mutationErrors, setMutationErrors] = useState();
  const [searchInvoiceNumber, setSearchInvoiceNumber] = useState('');

  const [getPaymentSelects, { data: selects }] = useLazyQuery(
    PAYMENT_SELECTS, {
      variables: { ids: [] },
      fetchPolicy: 'no-cache'
    }
  );

  const {
    register, control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({
      fields: FIELDS,
      resource: { bankDate: DateTime.now().toISODate() },
      nestedFields: [{
        nestedResources,
        prefix: 'txnLinesAttributes',
        fields: LINE_FIELDS,
      }],
    })
  });

  const [getTxnByReference] = useLazyQuery(TXN_BY_REFERENCE, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const txn = data.txnByReference;

      if (txn) {
        setValue('name', txn.entityName);
        setValue('entityType', txn.entityType);
        setValue('entityId', txn.entityId);

        const newInvoiceAmounts = [
          ...invoiceAmounts.filter(item => item.id !== txn.bankLine.id),
          {id: txn.bankLine.id, amount: txn.openBalance}
        ]

        setInvoiceAmounts(newInvoiceAmounts);
      }
    }
  });

  const handleSearchByReference = () => {
    if (searchInvoiceNumber) {
      getTxnByReference({ variables: { reference: searchInvoiceNumber } });
    }
  };

  const [createTxn] = useMutation(CREATE_TXN, {
    onCompleted: data => {
      if (data.createTxn.errors?.length) {
        setFormStatus(null);
        setMutationErrors(data.createTxn.errors);
      } else {
        refetch();
        setShowModal(false);
      }
    }
  });

  const onSubmit = useCallback(input => {
    setFormStatus('submitting');
    input.type = 'Payment';
    createTxn({
      variables: prepareTxnInput(
        input, FIELDS, { invoiceAmounts, accounts }
      )
    });
  });

  const watchAll = watch();
  console.log(watchAll);

  useEffect(() => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;

    if (txnLinesWatch) {
      const lastLine = txnLinesWatch[txnLinesWatch.length - 1];

      if (lastLine.creditLessDebit) {
        const prefix = `txnLinesAttributes.${nestedResources.length}`;

        register(`${prefix}.accountId`, { value: '' });
        register(`${prefix}.creditLessDebit`, { value: '' });
        register(`${prefix}.memo`, { value: '' });
        setNestedResources([...nestedResources, EMPTY_LINE]);
      }
    }
  }, [watchAll]);

  useEffect(() => {
    if (watchAll.entityId && watchAll.entityType) {
      getPaymentSelects({
        variables: {
          entityType: watchAll.entityType, entityId: watchAll.entityId
        }
      });
    }
  }, [watchAll.entityId, watchAll.entityType]);

  const fields = { ...FIELDS };
  const lineFields = { ...LINE_FIELDS };

  return (
    <View>
      <Text>
        Enter the name on the invoice below to show unpaid invoices
      </Text>
      <View
        style={{
          flexDirection: 'row',
          gap: 12,
          marginBottom: 12,
          alignItems: 'baseline'
        }}
      >
        <Text>or search for an invoice by number:</Text>

        <StyledTextInput
          value={searchInvoiceNumber}
          onChangeText={setSearchInvoiceNumber}
        />

        <Button label="Search" onPress={handleSearchByReference} />
      </View>

      <FormProvider watch={watch}>
        <Fields
          fields={fields}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        {selects?.invoiceLines && (
          <Section>
            <Text>Open Invoices</Text>

            <DataTable.Simple
              data={selects.invoiceLines}
              columns={invoiceColumns({ invoiceAmounts, setInvoiceAmounts })}
              component={minimalTable}
              pageLength="All"
            />
          </Section>
        )}

        <Section>
          <Text>Payments not connected to an invoice, if needed</Text>

          <NestedFields
            prefix="txnLinesAttributes"
            resources={nestedResources}
            fields={lineFields}
            control={control}
            errors={errors}
          />
        </Section>

        <MutationErrors errors={mutationErrors} />

        {'submitting' === formStatus ? (
          <Text>Saving...</Text>
        ) : (
          <ButtonGroup>
            <Button label="Save Payment" onPress={handleSubmit(onSubmit)} />
          </ButtonGroup>
        )}

      </FormProvider>
    </View>
  );
};

export default NewPayment;
